import type { Extension } from '@codemirror/state'
import { LanguageDescription, type LanguageSupport } from '@codemirror/language'
import { languages } from '@codemirror/language-data'
import { javascript } from '@codemirror/lang-javascript'
import { html } from '@codemirror/lang-html'
import { sql, SQLite } from '@codemirror/lang-sql'
import { python } from '@codemirror/lang-python'
import { css } from '@codemirror/lang-css'
import { json } from '@codemirror/lang-json'
import { markdown } from '@codemirror/lang-markdown'

/**
 * Syntax for the editor's `language` prop (highlight.js names, as the hosts
 * pass them). The languages PDApps are written in load with the editor; the
 * rest come from @codemirror/language-data on first use.
 */
const BUILTIN: Record<string, () => LanguageSupport> = {
  javascript: () => javascript(),
  typescript: () => javascript({ typescript: true }),
  html: () => html(),
  // papan.db runs SQLite.
  sql: () => sql({ dialect: SQLite }),
  python: () => python(),
  css: () => css(),
  json: () => json(),
  markdown: () => markdown({ codeLanguages: languages }),
}

/** highlight.js name → language-data name, where they differ. */
const ALIAS: Record<string, string> = {
  bash: 'shell', csharp: 'c#', cpp: 'c++', xml: 'xml', yaml: 'yaml', ini: 'properties',
  dockerfile: 'dockerfile', kotlin: 'kotlin', scss: 'sass',
}

/** The language extension available now, or null (then `loadLanguage` may find it). */
export function builtinLanguage(name: string | undefined): Extension | null {
  return name && BUILTIN[name] ? BUILTIN[name]() : null
}

/** Load a language from @codemirror/language-data (lazy chunk), or null when none matches. */
export async function loadLanguage(name: string | undefined): Promise<Extension | null> {
  if (!name) return null
  const desc = LanguageDescription.matchLanguageName(languages, ALIAS[name] ?? name, true)
  return desc ? desc.load() : null
}
